import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { SpecDetail } from '../types';
import SpecEditModal from './SpecEditModal';
import SpecGroupRow from './SpecGroupRow';
import AddSpecModalWithContext from './AddSpecModalWithContext';
import AddSpecModal, { SpecType } from './AddSpecModal';
import CreateSpecDetailModal from './CreateSpecDetailModal';

interface PackCopySpecsProps {
    specs: SpecDetail[];
    onSpecCreation: (specs: SpecDetail[]) => void;
    onSpecDelete?: (displayName: string) => void;
    autoOpenAddModal?: boolean;
    useContext?: boolean; // Whether to use context-based modals or local modals
}

const PackCopySpecs: React.FC<PackCopySpecsProps> = ({
    specs,
    onSpecCreation,
    onSpecDelete,
    autoOpenAddModal = false,
    useContext = true,
}) => {
    // State for tracking which group of specs is being edited
    const [editingGroup, setEditingGroup] = useState<{
        displayName: string;
        specs: SpecDetail[];
    } | null>(null);

    // State for modals
    const [isAddSpecModalOpen, setIsAddSpecModalOpen] =
        useState(autoOpenAddModal);
    const [isCreateSpecDetailModalOpen, setIsCreateSpecDetailModalOpen] =
        useState(false);
    const [newSpecDataForCreation, setNewSpecDataForCreation] = useState<{
        specType: SpecType;
        specNameValue: string;
        specNameLabel: string;
    } | null>(null);

    // Group specs by display_name
    const groupedSpecs: Record<string, SpecDetail[]> = {};
    specs.forEach((spec) => {
        const displayName = spec.display_name;
        if (!groupedSpecs[displayName]) {
            groupedSpecs[displayName] = [];
        }
        groupedSpecs[displayName].push(spec);
    });

    // Function to handle editing a group of specs
    const handleEditGroup = (displayName: string, specs: SpecDetail[]) => {
        if (useContext) {
            setEditingGroup({ displayName, specs });
        } else if (onSpecDelete) {
            // In local mode with delete handler, trigger delete
            onSpecDelete(displayName);
        } else {
            // Fallback - do nothing
            console.log('Edit/Delete functionality not available');
        }
    };

    // Determine which icon to use for the action button
    const getActionIcon = () => {
        if (!useContext && onSpecDelete) {
            return faTrash;
        }
        return undefined; // Use default (faPen) from SpecGroupRow
    };

    // Handler for the new spec modal's "Next" action
    const handleAddNewSpecNext = (
        specType: SpecType,
        specNameValue: string,
        specNameLabel: string,
    ) => {
        setNewSpecDataForCreation({ specType, specNameValue, specNameLabel });
        setIsAddSpecModalOpen(false);
        setIsCreateSpecDetailModalOpen(true);
    };

    // Handler for going back from CreateSpecDetailModal to AddSpecModal
    const handleBackFromCreateSpec = () => {
        setIsCreateSpecDetailModalOpen(false);
        setIsAddSpecModalOpen(true);
    };

    // Handler for saving the newly created spec
    const handleCreateSpecSave = (newSpecDetails: SpecDetail) => {
        if (useContext) {
            // Call the context handler, wrapping the single spec in an array
            onSpecCreation([newSpecDetails]);
        } else {
            // For local mode, add to existing specs
            const updatedSpecs = [...specs, newSpecDetails];
            onSpecCreation(updatedSpecs);
            // Automatically open the next spec creation modal
            setIsAddSpecModalOpen(true);
        }
        setIsCreateSpecDetailModalOpen(false);
        setNewSpecDataForCreation(null);
    };

    // Handler for saving and finishing (only used in local mode)
    const handleCreateSpecSaveAndFinish = (newSpecDetails: SpecDetail) => {
        if (useContext) {
            // In context mode, just save normally
            handleCreateSpecSave(newSpecDetails);
        } else {
            // For local mode, add to existing specs but don't open next modal
            const updatedSpecs = [...specs, newSpecDetails];
            onSpecCreation(updatedSpecs);
            setIsCreateSpecDetailModalOpen(false);
            setNewSpecDataForCreation(null);
        }
    };

    return (
        <>
            <button
                className="add-spec-btn"
                onClick={() => setIsAddSpecModalOpen(true)}
            >
                <FontAwesomeIcon icon={faPlus} />
                {specs.length === 0 ? 'Add First Spec' : 'Add Another Spec'}
            </button>

            {specs.length > 0 && (
                <table className="specs-table">
                    <tbody>
                        {Object.entries(groupedSpecs).map(
                            ([displayName, groupSpecs], groupIndex) => (
                                <SpecGroupRow
                                    key={groupIndex}
                                    displayName={displayName}
                                    specs={groupSpecs}
                                    onEdit={handleEditGroup}
                                    actionIcon={getActionIcon()}
                                />
                            ),
                        )}
                    </tbody>
                </table>
            )}

            {/* Edit Modal - only for context mode */}
            {editingGroup && useContext && (
                <SpecEditModal
                    specs={editingGroup.specs}
                    displayName={editingGroup.displayName}
                    onClose={() => setEditingGroup(null)}
                />
            )}

            {/* New Spec Modal */}
            {isAddSpecModalOpen && (
                <>
                    {useContext ? (
                        <AddSpecModalWithContext
                            onClose={() => {
                                setIsAddSpecModalOpen(false);
                                setNewSpecDataForCreation(null);
                            }}
                            onNext={handleAddNewSpecNext}
                            initialSpecType={newSpecDataForCreation?.specType}
                            initialSpecNameValue={
                                newSpecDataForCreation?.specNameValue
                            }
                        />
                    ) : (
                        <AddSpecModal
                            onClose={() => {
                                setIsAddSpecModalOpen(false);
                                setNewSpecDataForCreation(null);
                            }}
                            onNext={handleAddNewSpecNext}
                            initialSpecType={newSpecDataForCreation?.specType}
                            initialSpecNameValue={
                                newSpecDataForCreation?.specNameValue
                            }
                            existingSpecNames={specs.map((spec) => spec.name)}
                        />
                    )}
                </>
            )}

            {/* Use the new CreateSpecDetailModal */}
            {isCreateSpecDetailModalOpen && newSpecDataForCreation && (
                <CreateSpecDetailModal
                    specType={newSpecDataForCreation.specType}
                    specName={newSpecDataForCreation.specNameValue}
                    specLabel={newSpecDataForCreation.specNameLabel}
                    onClose={() => {
                        setIsCreateSpecDetailModalOpen(false);
                        setNewSpecDataForCreation(null);
                    }}
                    onSave={handleCreateSpecSave}
                    onSaveAndFinish={
                        useContext ? undefined : handleCreateSpecSaveAndFinish
                    }
                    onBack={handleBackFromCreateSpec}
                    isFirstSpec={specs.length === 0}
                />
            )}
        </>
    );
};

export default PackCopySpecs;
